/**
 * Add User — two-step flow matching Angular company "Add User" / UserManagementAddComponent (Identify User → form).
 * Phase 1: email + Search. Phase 2: separate modal with full "Add Role" form.
 *
 * Angular `UserManagementAddComponent` keeps one form: `(keyup)="onKey(userEmail.value)"` on Email — if the value
 * differs from the address used for the last `checkUserExists`, it clears role fields (`removeControls`). React uses
 * two modals; changing Email in phase 2 must reset and return to phase 1 so the user runs Search again.
 */
import { useState, useCallback, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { X, Loader2, AlertCircle } from "lucide-react"
import apiClient from "@/lib/api/apiClient"
import { showSuccess, showError, showValidationMessage, formatApiError } from "@/lib/utils/notifications"
import { useUserRole } from "@/hooks/useUserRole"
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/SearchableSelect"
import { cn } from "@/lib/utils/helpers"
import modalStyles from "@/features/application/styles/modals.module.css"
import companyFormStyles from "@/features/company/styles/companyAdd.module.css"
import addUserStyles from "@/features/company/styles/addUserModal.module.css"
import {
  digitsOnlyPhone,
  isValidPropertyPhoneDigits,
  maskPhoneOnChange,
  PROPERTY_PHONE_MAX_DIGITS,
} from "@/features/property/utils/formatPhone"
import { PHONE_FORMAT_ERROR } from '@/features/company/constants'
import {
  ADD_USER_EMAIL_REGEX,
  ADD_USER_FIN_MGR_ROLES,
  ADD_USER_NAME_PATTERN,
  COMPANY_ROLE_NAMES,
  PROPERTY_STAFF_ROLE_NAMES,
} from '../constants'
import type { AddUserModalProps } from '../types'

function isAddUserFinMgrRole(role: string): boolean {
  return (ADD_USER_FIN_MGR_ROLES as readonly string[]).includes(role)
}

/** Store at most `PROPERTY_PHONE_MAX_DIGITS` (10–13) — same as Property Contacts / `formatPhone.ts`. */
function cleanPhoneDigits(value: string): string {
  return digitsOnlyPhone(value).slice(0, PROPERTY_PHONE_MAX_DIGITS)
}

function getConfirmEmailFieldError(
  userNotFound: boolean,
  primaryEmail: string,
  confirmValue: string,
): string {
  if (!userNotFound) return ""
  const c = confirmValue.trim()
  if (!c) return "Confirm Email is required."
  if (!ADD_USER_EMAIL_REGEX.test(c)) return "Confirm Email is not valid."
  if (c.toLowerCase() !== primaryEmail.trim().toLowerCase())
    return "Email and Confirm Email not Matched."
  return ""
}

const inputClass = (err?: string) =>
  cn(
    "w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm transition",
    "placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/30 focus:border-[rgb(21_151_255)]",
    err ? "border-rose-400" : "border-slate-200",
  )

const labelClass = "mb-1.5 block text-sm font-medium text-slate-700"
const requiredMark = <span className="font-semibold text-[#dc2626]">*</span>

export function AddUserModal({
  isOpen,
  onClose,
  companyId,
  onSuccess,
  variant = "company",
  propertyId: propertyScopeId,
  transferNotesId,
}: AddUserModalProps) {
  const { isLibertyRole } = useUserRole()
  const isPropertyStaff = variant === "propertyStaff"
  const roleOptions = isPropertyStaff ? PROPERTY_STAFF_ROLE_NAMES : COMPANY_ROLE_NAMES

  const [phase, setPhase] = useState<"identify" | "details">("identify")
  const [email, setEmail] = useState("")
  const [confirmEmail, setConfirmEmail] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [phone, setPhone] = useState("")
  const [jobTitle, setJobTitle] = useState("")
  const [roleNameField, setRoleNameField] = useState("")
  const [propertyId, setPropertyId] = useState("")
  const [propertyOptions, setPropertyOptions] = useState<SearchableSelectOption[]>([])
  const [propertiesLoading, setPropertiesLoading] = useState(false)
  const [activateUser, setActivateUser] = useState(false)
  const [finMgrAttribute, setFinMgrAttribute] = useState(false)
  const [searching, setSearching] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [userId, setUserId] = useState<number | null>(null)
  const [userNotFound, setUserNotFound] = useState(false)
  /** While true, Confirm Email is `readOnly` so Chrome/Safari cannot autofill it and fire `onChange` (which was copying Email into state). */
  const [confirmEmailLocked, setConfirmEmailLocked] = useState(true)
  /** Normalized email from last successful `runEmailLookup` — Angular `userEmail` / `onKey` parity. */
  const lookupEmailSnapshotRef = useRef<string | null>(null)
  const [emailError, setEmailError] = useState("")
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  const loadProperties = useCallback(async (q?: string) => {
    const cid = Number(companyId)
    if (!cid) return
    setPropertiesLoading(true)
    try {
      const params: Record<string, string> = { company_id: String(cid) }
      if (q?.trim()) params.q = q.trim()
      const res = await apiClient.get("/api/all_property", { params })
      const raw = res.data as unknown
      let list: Array<{ property_id?: number; property_name?: string }> = []
      if (Array.isArray(raw)) {
        list = raw as Array<{ property_id?: number; property_name?: string }>
      } else if (raw && typeof raw === "object") {
        const o = raw as Record<string, unknown>
        if (Array.isArray(o.data)) list = o.data as typeof list
        else if (Array.isArray(o.properties)) list = o.properties as typeof list
        else if (Array.isArray(o.results)) list = o.results as typeof list
      }
      const opts: SearchableSelectOption[] = list.map((p) => ({
        value: String(p.property_id ?? ""),
        label: String(p.property_name ?? p.property_id ?? ""),
      })).filter((o) => o.value !== "")
      setPropertyOptions(opts)
      if (opts.length === 1) {
        setPropertyId((prev) => prev || opts[0].value)
      }
    } catch {
      setPropertyOptions([])
    } finally {
      setPropertiesLoading(false)
    }
  }, [companyId])

  /** Angular `searchProperty`: only refetch when search length is even — cuts API churn while typing. */
  const loadPropertiesForSearch = useCallback(
    (q?: string) => {
      const t = (q ?? "").trim()
      if (t.length > 0 && t.length % 2 !== 0) return
      void loadProperties(q)
    },
    [loadProperties],
  )

  useEffect(() => {
    if (!isOpen || phase !== "details") return
    loadProperties()
  }, [isOpen, phase, loadProperties])

  useEffect(() => {
    if (roleNameField !== "ROLE_REGIONAL_MANAGER") {
      setPropertyId("")
    }
  }, [roleNameField])

  /** Reload list when user picks Regional Manager (options may have been empty or stale). */
  useEffect(() => {
    if (roleNameField !== "ROLE_REGIONAL_MANAGER") return
    if (!isOpen || phase !== "details") return
    void loadProperties()
  }, [roleNameField, isOpen, phase, loadProperties])

  const resetAll = () => {
    lookupEmailSnapshotRef.current = null
    setPhase("identify")
    setEmail("")
    setConfirmEmail("")
    setFirstName("")
    setLastName("")
    setPhone("")
    setJobTitle("")
    setRoleNameField("")
    setPropertyId("")
    setPropertyOptions([])
    setActivateUser(false)
    setFinMgrAttribute(false)
    setUserId(null)
    setUserNotFound(false)
    setConfirmEmailLocked(true)
    setEmailError("")
    setFormErrors({})
  }

  /**
   * Angular `onKey`: when Email text differs from the last lookup, `removeControls()` — here we return to Search step.
   */
  const revertToIdentifyOnEmailChange = useCallback((nextEmail: string) => {
    lookupEmailSnapshotRef.current = null
    setPhase("identify")
    setEmail(nextEmail)
    setConfirmEmail("")
    setFirstName("")
    setLastName("")
    setPhone("")
    setJobTitle("")
    setRoleNameField("")
    setPropertyId("")
    setPropertyOptions([])
    setActivateUser(false)
    setFinMgrAttribute(false)
    setUserId(null)
    setUserNotFound(false)
    setConfirmEmailLocked(true)
    setFormErrors({})
    setEmailError("")
  }, [])

  const handleClose = () => {
    resetAll()
    onClose()
  }

  const validateIdentify = (): boolean => {
    const e = email.trim().toLowerCase()
    if (!e) {
      setEmailError("Email is required.")
      return false
    }
    if (!ADD_USER_EMAIL_REGEX.test(e)) {
      setEmailError("Email is not valid.")
      return false
    }
    setEmailError("")
    return true
  }

  /**
   * Email lookup — Angular `checkUserExists` parity: any HTTP error (except Liberty email rule)
   * opens the new-user path; success prefills existing user.
   */
  const runEmailLookup = async () => {
    if (!validateIdentify()) return
    setSearching(true)
    const lookup = email.trim().toLowerCase()
    try {
      const res = await apiClient.get(`/api/email?email=${encodeURIComponent(lookup)}`)
      const data = res?.data ?? res

      if (data?.liberty_role && !isLibertyRole) {
        showError("Cannot Add Role To Liberty Users")
        return
      }

      /** Angular `checkUserExists` success branch: response always means a user record exists (`user_id` set).
       *  If API returns 200 with no `user_id`, treat as new email (same as Angular **error** branch → `userNotRecordFound`). */
      const rawId = data?.user_id
      const hasExistingUser =
        rawId != null && rawId !== "" && !(typeof rawId === "number" && Number.isNaN(rawId))
      setUserId(hasExistingUser ? Number(rawId) : null)
      setUserNotFound(!hasExistingUser)
      if (hasExistingUser) {
        setFirstName(data.first_name ?? "")
        setLastName(data.last_name ?? "")
        setPhone(cleanPhoneDigits(String(data.mobile_no ?? "")))
        setJobTitle(data.job_title ?? "")
      } else {
        setFirstName("")
        setLastName("")
        setPhone("")
        setJobTitle("")
        setConfirmEmail("")
        setConfirmEmailLocked(true)
      }
      lookupEmailSnapshotRef.current = lookup
      setPhase("details")
    } catch {
      if (lookup.includes("@libertyrent") && !isLibertyRole) {
        showError("Cannot Add Liberty User")
        return
      }
      setUserId(null)
      setUserNotFound(true)
      setFirstName("")
      setLastName("")
      setPhone("")
      setJobTitle("")
      /** Angular `checkUserExists` error branch: `confirm_email` stays empty — user must re-type (see UserManagementAddComponent). */
      setConfirmEmail("")
      setConfirmEmailLocked(true)
      lookupEmailSnapshotRef.current = lookup
      setPhase("details")
    } finally {
      setSearching(false)
    }
  }

  const onIdentifyFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    void runEmailLookup()
  }

  const validateDetails = (): boolean => {
    const err: Record<string, string> = {}
    const confirmErr = getConfirmEmailFieldError(userNotFound, email, confirmEmail)
    if (confirmErr) err.confirm_email = confirmErr
    if (!firstName.trim()) err.first_name = "First Name is required."
    else if (!ADD_USER_NAME_PATTERN.test(firstName.trim())) err.first_name = "Enter Valid First Name"
    if (!lastName.trim()) err.last_name = "Last Name is required."
    else if (!ADD_USER_NAME_PATTERN.test(lastName.trim())) err.last_name = "Enter Valid Last Name"
    if (!phone.trim()) err.phone = "Contact Phone is required."
    else if (!isValidPropertyPhoneDigits(phone)) err.phone = PHONE_FORMAT_ERROR
    if (!roleNameField) err.role_name = "Role Selection is required."
    if (
      !isPropertyStaff &&
      roleNameField === "ROLE_REGIONAL_MANAGER" &&
      !propertyId.trim()
    ) {
      err.property_id = "Property Selection is required."
    }
    setFormErrors(err)
    return Object.keys(err).length === 0
  }

  const handleAddRole = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateDetails()) return
    setSubmitting(true)
    try {
      let targetUserId = userId
      if (targetUserId == null) {
        // Match Angular getFilterValue() on POST /api/user (email + confirm_email, role_name, activate_user, …).
        const createBody: Record<string, unknown> = {
          email: email.trim(),
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          mobile_no: cleanPhoneDigits(phone),
          role_name: roleNameField,
        }
        if (userNotFound) createBody.confirm_email = confirmEmail.trim()
        const jt = jobTitle.trim()
        if (jt) createBody.job_title = jt
        if (userNotFound && activateUser) createBody.activate_user = true
        if (finMgrAttribute && isAddUserFinMgrRole(roleNameField)) {
          createBody.fin_mgr_attribute = true
        }
        const createRes = await apiClient.post("/api/user", createBody)
        const raw = createRes?.data ?? createRes
        targetUserId =
          typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw) : Number((raw as { user_id?: number })?.user_id)
      }
      const payload: Record<string, unknown> = {
        role_name: roleNameField,
        company_id: Number(companyId),
      }
      /** Backend / Angular parity: property-scoped role from transfer wizard expects `transfer_property: true` on POST /api/user/:id/roles. */
      if (isPropertyStaff && propertyScopeId != null && String(propertyScopeId).trim() !== "") {
        payload.property_id = String(Number(propertyScopeId))
        payload.transfer_property = true
      } else if (roleNameField === "ROLE_REGIONAL_MANAGER" && propertyId.trim()) {
        payload.property_id = String(Number(propertyId))
      }
      if (transferNotesId != null && String(transferNotesId).trim() !== "") {
        payload.transfer_notes = transferNotesId
      }
      /** Angular `user-management-add.component.ts` `addUserRole`: only sets `send_email` when explicitly false — do not send `true`. */
      if (userNotFound && !activateUser) payload.send_email = false
      if (finMgrAttribute && isAddUserFinMgrRole(roleNameField)) {
        payload.fin_mgr_attribute = true
      }
      await apiClient.post(`/api/user/${targetUserId}/roles`, payload)
      showSuccess("User Role Added Successfully!")
      onSuccess?.()
      handleClose()
    } catch (err: unknown) {
      /** Same UX as property Contacts add-user / Angular validation: full API message (e.g. duplicate role "Already exists"). */
      showValidationMessage(formatApiError(err), "Validation Message")
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    if (!isOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prev
    }
  }, [isOpen])

  if (!isOpen) return null

  if (phase === "identify") {
    const identifyTitle = isPropertyStaff ? "Add Employee" : "Identify User"
    const identify = (
      <div
        data-testid="company-add-user-identify-overlay"
        className={`${modalStyles.modalOverlay} !z-[10001]`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="identify-user-title"
        onClick={handleClose}
      >
        <div
          data-testid="company-add-user-identify-panel"
          className={`${modalStyles.modalContent} ${modalStyles.modalContent640} ${modalStyles.modalContentStacked}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={addUserStyles.header}>
            <div className={addUserStyles.headerLead}>
              <div className={addUserStyles.titleText}>
                <h2 id="identify-user-title" className={addUserStyles.title}>
                  {identifyTitle}
                </h2>
              </div>
            </div>
            <button
              data-testid="company-add-user-identify-close-button"
              type="button"
              onClick={handleClose}
              className={`${modalStyles.closeButton} ${modalStyles.modalValidationClose}`}
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <form onSubmit={onIdentifyFormSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className={addUserStyles.identifyBody}>
              <div>
                <label htmlFor="add-user-email" className={labelClass}>
                  Email{requiredMark}
                </label>
                <input
                  data-testid="company-add-user-email-input"
                  id="add-user-email"
                  type="email"
                  autoComplete="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    setEmailError("")
                  }}
                  className={inputClass(emailError)}
                />
                {emailError ? (
                  <div className={companyFormStyles.errorMessage} role="alert">
                    <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
                    {emailError}
                  </div>
                ) : null}
              </div>
            </div>
            <div className={`${addUserStyles.footer} ${addUserStyles.footerCentered}`}>
              <div className={`${addUserStyles.footerActions} ${modalStyles.modalFooterActionGroup}`}>
                <button
                  data-testid="company-add-user-search-button"
                  type="button"
                  disabled={searching}
                  onClick={(ev) => {
                    ev.preventDefault()
                    ev.stopPropagation()
                    void runEmailLookup()
                  }}
                  className={modalStyles.modalActionPrimary}
                >
                  {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Search
                </button>
                <button
                  data-testid="company-add-user-identify-cancel-button"
                  type="button"
                  onClick={(ev) => {
                    ev.stopPropagation()
                    handleClose()
                  }}
                  className={modalStyles.modalActionSecondary}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    )
    return createPortal(identify, document.body)
  }

  const detailsTitle = isPropertyStaff ? "Add Employee" : "Identify User"

  const details = (
    <div
      data-testid="company-add-user-details-overlay"
      className={`${modalStyles.modalOverlay} !z-[10001]`}
      role="presentation"
      onClick={handleClose}
    >
      <div
        data-testid="company-add-user-details-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-role-title"
        className={`${modalStyles.modalContent} ${modalStyles.modalContent640} ${modalStyles.modalContentStacked} max-h-[min(90vh,calc(100vh-4rem))]`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={addUserStyles.header}>
          <div className={addUserStyles.titleWrap}>
            <div className={addUserStyles.titleText}>
              <h2 id="add-role-title" className={addUserStyles.title}>
                {detailsTitle}
              </h2>
            </div>
          </div>
          <button
            data-testid="company-add-user-details-close-button"
            type="button"
            onClick={handleClose}
            className={`${modalStyles.closeButton} ${modalStyles.modalValidationClose}`}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

          <form
            data-testid="company-add-user-details-form"
            onSubmit={handleAddRole}
            className="flex min-h-0 flex-1 flex-col"
            autoComplete="off"
          >
            <div className={addUserStyles.body}>
              <section className={addUserStyles.section}>
                
                <div className={userNotFound ? addUserStyles.fieldGridTwo : "grid grid-cols-1 gap-3.5"}>
                  <div>
                    <label htmlFor="add-user-email-details" className={labelClass}>
                      Email{requiredMark}
                    </label>
                    <input
                      id="add-user-email-details"
                      data-testid="company-add-user-email-details-input"
                      type="text"
                      inputMode="email"
                      autoComplete="off"
                      data-lpignore="true"
                      placeholder="Email"
                      title="Changing the address returns you to Search to verify again (Angular parity)."
                      value={email}
                      onChange={(e) => {
                        const next = e.target.value
                        const snap = lookupEmailSnapshotRef.current
                        const nextNorm = next.trim().toLowerCase()
                        if (snap != null && nextNorm !== snap) {
                          revertToIdentifyOnEmailChange(next)
                          return
                        }
                        setEmail(next)
                        if (userNotFound) {
                          setFormErrors((p) => ({ ...p, confirm_email: "" }))
                        }
                      }}
                      className={cn(inputClass(), "bg-white text-slate-900")}
                    />
                  </div>
                  {userNotFound ? (
                    <div>
                      <label htmlFor="add-user-confirm-email" className={labelClass}>
                        Confirm Email{requiredMark}
                      </label>
                      <input
                        data-testid="company-add-user-confirm-email-input"
                        type="text"
                        inputMode="email"
                        id="add-user-confirm-email"
                        name="add_user_confirm_contact"
                        placeholder="Confirm Email"
                        autoComplete="off"
                        readOnly={confirmEmailLocked}
                        value={confirmEmail}
                        onFocus={() => {
                          if (!confirmEmailLocked) return
                          setConfirmEmailLocked(false)
                          setConfirmEmail("")
                          setFormErrors((p) => ({ ...p, confirm_email: "" }))
                        }}
                        onChange={(e) => {
                          setConfirmEmail(e.target.value)
                          setFormErrors((p) => ({ ...p, confirm_email: "" }))
                        }}
                        className={inputClass(formErrors.confirm_email)}
                      />
                      {formErrors.confirm_email ? (
                        <div className={companyFormStyles.errorMessage} role="alert">
                          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
                          {formErrors.confirm_email}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </section>

              <section className={addUserStyles.section}>
                
                <div className={addUserStyles.fieldGridTwo}>
                  <div>
                    <label className={labelClass}>First Name{requiredMark}</label>
                    <input
                      data-testid="company-add-user-first-name-input"
                      type="text"
                      placeholder="First Name"
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value)
                        setFormErrors((p) => ({ ...p, first_name: "" }))
                      }}
                      className={inputClass(formErrors.first_name)}
                    />
                    {formErrors.first_name ? (
                      <div className={companyFormStyles.errorMessage} role="alert">
                        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
                        {formErrors.first_name}
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <label className={labelClass}>Last Name{requiredMark}</label>
                    <input
                      data-testid="company-add-user-last-name-input"
                      type="text"
                      placeholder="Last Name"
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value)
                        setFormErrors((p) => ({ ...p, last_name: "" }))
                      }}
                      className={inputClass(formErrors.last_name)}
                    />
                    {formErrors.last_name ? (
                      <div className={companyFormStyles.errorMessage} role="alert">
                        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
                        {formErrors.last_name}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Phone{requiredMark}</label>
                  <input
                    data-testid="company-add-user-phone-input"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="(xxx) xxx-xxxx"
                    value={maskPhoneOnChange(phone)}
                    onChange={(e) => {
                      setPhone(cleanPhoneDigits(e.target.value))
                      setFormErrors((p) => ({ ...p, phone: "" }))
                    }}
                    className={inputClass(formErrors.phone)}
                  />
                  {formErrors.phone ? (
                    <div className={companyFormStyles.errorMessage} role="alert">
                      <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
                      {formErrors.phone}
                    </div>
                  ) : null}
                </div>
                <div>
                  <label className={labelClass}>Job Title (optional)</label>
                  <input
                    data-testid="company-add-user-job-title-input"
                    type="text"
                    placeholder="Job Title"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    className={inputClass()}
                  />
                </div>
              </section>

              <section className={addUserStyles.section}>
                
                <div>
                  <label className={labelClass}>Assign Role{requiredMark}</label>
                  <select
                    data-testid="company-add-user-role-select"
                    value={roleNameField}
                    onChange={(e) => {
                      const v = e.target.value
                      setRoleNameField(v)
                      if (!isAddUserFinMgrRole(v)) setFinMgrAttribute(false)
                      setFormErrors((p) => ({ ...p, role_name: "", property_id: "" }))
                    }}
                    className={inputClass(formErrors.role_name)}
                  >
                    <option value="">Select Role</option>
                    {Object.entries(roleOptions).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  {formErrors.role_name ? (
                    <div className={companyFormStyles.errorMessage} role="alert">
                      <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
                      {formErrors.role_name}
                    </div>
                  ) : null}
                </div>

                {!isPropertyStaff && roleNameField === "ROLE_REGIONAL_MANAGER" ? (
                  <div>
                    <label className={labelClass}>Select Property{requiredMark}</label>
                    <SearchableSelect
                      options={propertyOptions}
                      value={propertyId}
                      onChange={(v) => {
                        setPropertyId(typeof v === "string" ? v : v[0] ?? "")
                        setFormErrors((p) => ({ ...p, property_id: "" }))
                      }}
                      placeholder="Search by property name"
                      searchPlaceholder="Search properties…"
                      onOpen={() => {
                        void loadProperties()
                      }}
                      onSearch={(text) => loadPropertiesForSearch(text)}
                      searchDebounceMs={300}
                      loading={propertiesLoading}
                      error={formErrors.property_id}
                      className="w-full"
                      triggerDataTestId="company-add-user-property-trigger"
                    />
                    {formErrors.property_id ? (
                      <div className={companyFormStyles.errorMessage} role="alert">
                        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
                        {formErrors.property_id}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {(userNotFound || isAddUserFinMgrRole(roleNameField)) ? (
                  <div className={addUserStyles.optionsPanel}>
                    {userNotFound ? (
                      <label className={addUserStyles.checkboxLabel}>
                        <input
                          data-testid="company-add-user-activate-checkbox"
                          type="checkbox"
                          id="activate_user"
                          checked={activateUser}
                          onChange={(e) => setActivateUser(e.target.checked)}
                        />
                        <span>Activate Account & Send Welcome Email</span>
                      </label>
                    ) : null}

                    {isAddUserFinMgrRole(roleNameField) ? (
                      <label className={addUserStyles.checkboxLabel}>
                        <input
                          type="checkbox"
                          id="add-user-fin-mgr"
                          data-testid="company-add-user-fin-mgr-checkbox"
                          checked={finMgrAttribute}
                          onChange={(e) => setFinMgrAttribute(e.target.checked)}
                        />
                        <span>Finance Manager</span>
                      </label>
                    ) : null}
                  </div>
                ) : null}
              </section>
            </div>

            <div className={`${addUserStyles.footer} ${addUserStyles.footerCentered}`}>
              <div className={`${addUserStyles.footerActions} ${modalStyles.modalFooterActionGroup}`}>
                <button
                  data-testid="company-add-user-submit-button"
                  type="submit"
                  disabled={submitting}
                  className={modalStyles.modalActionPrimary}
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Add Role
                </button>
                <button
                  data-testid="company-add-user-details-cancel-button"
                  type="button"
                  onClick={handleClose}
                  className={modalStyles.modalActionSecondary}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
      </div>
    </div>
  )

  return createPortal(details, document.body)
}
